import React, { useMemo } from 'react';

import {
  ERROR_TYPE,
  FEATURES,
  MISCELLANEOUS_ERROR_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import { useMediaQuery } from '@context/mediaQuery';
import { RefetchOnComplete } from '@graphql/helpers/effects';
import {
  AudienceDocument,
  MinimalReleaseUpdatesDocument,
  ReleasePageDocument,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import type { ReleaseCollectorCursorConnectionSort, TierNft } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';

import { AudienceSeat } from './Seat';
import { FIRST_ROW, FOURTH_ROW, SECOND_ROW, THIRD_ROW } from './constants';

import type { AudienceSortType } from './constants';

const INITIAL_ITEMS = 34;
const ITEMS_PER_PAGE = 51;

function getSortByTier(
  tier: TierNft,
  sort: AudienceSortType,
): ReleaseCollectorCursorConnectionSort | undefined {
  const isTop = sort === 'top';
  const earliest: ReleaseCollectorCursorConnectionSort = {
    priorityFirstNftDate: 'ASC',
  };

  switch (tier) {
    case 'GA': {
      return isTop
        ? {
            gaNftsCount: 'DESC',
          }
        : earliest;
    }
    case 'ALL':
      return isTop
        ? {
            priorityTop: 'DESC',
          }
        : earliest;
    // "vipNftsCount" sort has tie breaker on total amount of nfts (w/ ga), but we always prioritize vip nfts
    case 'VIP': {
      return isTop
        ? {
            vipNftsCount: 'DESC',
          }
        : earliest;
    }
    default: {
      return;
    }
  }
}

RefetchOnComplete({
  trigger: MinimalReleaseUpdatesDocument,
  refetch: [AudienceDocument, ReleasePageDocument],
  throttled: '3 seconds',
});

export function useAudience({
  releaseId,
  tier,
  sort,
  paginate,
}: {
  releaseId: string;
  tier: TierNft;
  sort: AudienceSortType;
  paginate: boolean;
}) {
  const { tablet } = useMediaQuery();

  const logError = useLogError();

  const {
    orderedList: collectors,
    error,
    isLoadingNewPage: loading,
    loadMoreNextPage: fetchMore,
    hasNextPage = false,
    isFetchingNextPage,
  } = useInfiniteQuery(AudienceDocument, {
    filterQueryKey: {
      releaseId,
      tier,
      sort,
    },
    getNextPageParam({ data }) {
      return (
        data.release?.collectors.pageInfo.hasNextPage && {
          after: data.release.collectors.pageInfo.endCursor,
        }
      );
    },
    variables({ pageParam }) {
      return {
        releaseId,
        pagination: {
          sort: getSortByTier(tier, sort),
          first: paginate ? ITEMS_PER_PAGE : INITIAL_ITEMS,
          after: pageParam?.after || null,
          tier,
        },
      };
    },
    list({ release }) {
      return release?.collectors.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    onError(error) {
      logError({
        action: MISCELLANEOUS_ERROR_ACTIONS.AUDIENCE_FETCH_ERROR,
        error,
        errorType: ERROR_TYPE.FETCH_GQL_ERROR,
        isArtistOnlyPage: false,
        level: 'error',
        message: 'Error calling useAudience hook',
        feature: FEATURES.AUDIENCE,
        indexedTags: {
          releaseId,
        },
      });
    },
    staleTime: 0,
  });

  const firstRow = useMemo(() => {
    return collectors
      .slice(0, FIRST_ROW)
      .map(collector => (
        <AudienceSeat
          key={`audience-seat-${collector.id}`}
          seatFrag={collector}
          tier={tier}
          sort={sort}
        />
      ));
  }, [collectors, sort, tier]);

  const secondRow = useMemo(() => {
    return collectors
      .slice(FIRST_ROW, SECOND_ROW)
      .map(collector => (
        <AudienceSeat
          key={`audience-seat-${collector.id}`}
          seatFrag={collector}
          tier={tier}
          sort={sort}
        />
      ));
  }, [collectors, sort, tier]);

  const thirdRow = useMemo(() => {
    return collectors
      .slice(SECOND_ROW, THIRD_ROW)
      .map(collector => (
        <AudienceSeat
          key={`audience-seat-${collector.id}`}
          seatFrag={collector}
          tier={tier}
          sort={sort}
        />
      ));
  }, [collectors, sort, tier]);

  const fourthRow = useMemo(() => {
    return collectors
      .slice(THIRD_ROW, FOURTH_ROW)
      .map(collector => (
        <AudienceSeat
          key={`audience-seat-${collector.id}`}
          seatFrag={collector}
          tier={tier}
          sort={sort}
        />
      ));
  }, [collectors, sort, tier]);

  const remainingRows = useMemo(() => {
    const remaining = paginate
      ? collectors.slice(tablet ? FOURTH_ROW : FIRST_ROW)
      : collectors.slice(tablet ? FOURTH_ROW : FIRST_ROW, tablet ? FOURTH_ROW + 9 : FIRST_ROW + 24);

    return remaining.map(collector => (
      <AudienceSeat
        key={`audience-seat-${collector.id}`}
        seatFrag={collector}
        tier={tier}
        sort={sort}
      />
    ));
  }, [collectors, paginate, sort, tablet, tier]);

  return {
    rows: {
      firstRow,
      secondRow,
      thirdRow,
      fourthRow,
      remainingRows,
    },
    collectors,
    loading,
    hasNextPage,
    fetchMore,
    error,
    isFetchingNextPage,
  };
}
